import React, { useState } from 'react';
import './index.css';
import { submitLead } from './firebase';

function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{name?: string; contact?: string}>({});

  const validateForm = () => {
    const newErrors: {name?: string; contact?: string} = {};

    if (!formData.name.trim() || formData.name.trim().length < 2) {
      newErrors.name = 'Please enter your name';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const hasValidPhone = phoneDigits.length === 10;

    const hasValidEmail = formData.email && emailRegex.test(formData.email);

    if (!hasValidEmail && !hasValidPhone) {
      newErrors.contact = 'Please enter a valid email or phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await submitLead(formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting lead:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-vault-black/90 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <VaultLogo />
            <span className="text-xl font-semibold tracking-wider">VAULT DESIGN</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#process" className="text-gray-300 hover:text-white transition">Process</a>
            <a href="#contact" className="btn-primary text-sm py-3 px-6">Get Started</a>
          </div>
        </div>
      </nav>

      {/* Hero Section - StoryBrand: Clear value proposition */}
      <section className="h-screen relative overflow-hidden">
        {/* Stone texture background */}
        <div className="absolute inset-0 bg-vault-black stone-texture" />

        {/* Hero video with overlay */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            style={{ transform: 'scale(0.7)' }}
          >
            <source src="/hero-video-optimized.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-vault-black/60" />
        </div>

        <div className="relative z-10 h-full flex items-center justify-center px-4">
          <div className="w-full max-w-2xl text-center mx-auto">
            <p className="text-vault-gold text-sm md:text-base tracking-[0.3em] uppercase mb-4">
              Custom Luxury Home Gyms
            </p>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-semibold mb-4 md:mb-8 leading-tight">
              Your Home Deserves<br />
              <span className="text-vault-gold">a Better Gym</span>
            </h1>
            <p className="text-lg md:text-2xl text-gray-300 max-w-xl mb-6 md:mb-12 leading-relaxed mx-auto">
              A gym you'll actually want to use—designed around you, built to last.
            </p>
            <div className="flex flex-row gap-3 justify-center">
              <a href="#contact" className="btn-primary text-sm md:text-lg py-3 px-4 md:py-4 md:px-8">
                Start Your Project
              </a>
              <a href="#process" className="btn-secondary text-sm md:text-lg py-3 px-4 md:py-4 md:px-8">
                Our Process
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-10">
          <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Problem Section - StoryBrand: Identify the problem */}
      <section className="py-24 bg-vault-dark">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-6">
              Most Home Gyms Are an Afterthought
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              You spent years building your dream home. Why settle for a basement
              full of mismatched equipment?
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <ProblemCard
              icon={<IconHome />}
              title="Generic Spaces"
              description="Equipment dumped in a spare room doesn't reflect your home's quality or your standards."
            />
            <ProblemCard
              icon={<IconDollar />}
              title="Wasted Investment"
              description="Expensive equipment that sits unused because the space doesn't inspire you to train."
            />
            <ProblemCard
              icon={<IconBlueprint />}
              title="Poor Planning"
              description="Layout issues, inadequate ventilation, wrong flooring—problems that surface after it's too late."
            />
          </div>
        </div>
      </section>

      {/* Guide Section - Hidden for now
      <section className="py-24 bg-vault-black">
        ...
      </section>
      */}

      {/* Process Section - StoryBrand: Give them a plan */}
      <section id="process" className="py-24 bg-vault-black">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-vault-gold text-sm tracking-[0.2em] uppercase mb-4">
              The Process
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold mb-6">
              Three Steps to Your<br />Dream Gym
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              A clear path from vision to reality.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <ProcessStep
              number="01"
              title="Consult & Design"
              description="We learn your fitness goals, assess your space, and create a custom design that fits your home's aesthetic."
            />
            <ProcessStep
              number="02"
              title="Build & Install"
              description="We source premium equipment and handle complete installation—flooring, lighting, ventilation, everything."
            />
            <ProcessStep
              number="03"
              title="Train & Enjoy"
              description="Walk into a gym you're proud of. A space that motivates you every single day."
            />
          </div>
        </div>
      </section>

      {/* Work Section - Hidden until we have real projects
      <section id="work" className="py-24 bg-vault-black">
        ...
      </section>
      */}

      {/* Stakes Section - StoryBrand: What's at stake (failure vs success) */}
      <section className="py-24 bg-vault-dark">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <div className="p-8 border border-red-500/20 rounded-lg bg-red-500/5">
              <h3 className="text-2xl font-semibold mb-6 text-red-400">Without a Plan</h3>
              <ul className="space-y-4 text-gray-400">
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  A gym that looks like an afterthought
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  Equipment that collects dust
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  Wasted square footage in your luxury home
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  Regret every time you see the space
                </li>
              </ul>
            </div>
            <div className="p-8 border border-vault-gold/30 rounded-lg bg-vault-gold/5">
              <h3 className="text-2xl font-semibold mb-6 text-vault-gold">With Vault Design</h3>
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="text-vault-gold mt-1">✓</span>
                  A gym that matches your home's caliber
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-vault-gold mt-1">✓</span>
                  A space you actually want to train in
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-vault-gold mt-1">✓</span>
                  Added value to your property
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-vault-gold mt-1">✓</span>
                  Pride every time you walk in
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - StoryBrand: Call to action */}
      <section id="contact" className="py-24 bg-vault-black">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-semibold mb-6">
              Ready to Build Your<br />
              <span className="text-vault-gold">Dream Gym?</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Call, text, or book a time that works for you.
            </p>
          </div>

          {/* Quick contact options */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a href="tel:+18014731380" className="flex items-center justify-center gap-3 bg-vault-charcoal border border-white/10 rounded-lg px-6 py-4 hover:border-vault-gold/50 transition">
              <svg className="w-6 h-6 text-vault-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="text-white font-medium">Call or Text</span>
            </a>
            <a href="https://calendly.com/vaultdesigngyms/30min" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-vault-charcoal border border-white/10 rounded-lg px-6 py-4 hover:border-vault-gold/50 transition">
              <svg className="w-6 h-6 text-vault-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-white font-medium">Book a Call</span>
            </a>
          </div>

          <div className="text-center text-gray-500 mb-8">— or leave your info —</div>

          {submitted ? (
            <div className="bg-vault-charcoal border border-vault-gold/30 rounded-lg p-12 text-center">
              <div className="w-16 h-16 bg-vault-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-vault-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold mb-4">We'll Be in Touch</h3>
              <p className="text-gray-400">
                Thanks for reaching out. We'll contact you within 24 hours to discuss your project.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-vault-charcoal border border-white/10 rounded-lg p-8 md:p-12">
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full bg-vault-dark border rounded px-4 py-3 text-white focus:outline-none focus:border-vault-gold transition ${errors.name ? 'border-red-500' : 'border-white/10'}`}
                    placeholder="Your name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full bg-vault-dark border rounded px-4 py-3 text-white focus:outline-none focus:border-vault-gold transition ${errors.contact && !formData.phone ? 'border-red-500' : 'border-white/10'}`}
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      let formatted = digits;
                      if (digits.length > 6) {
                        formatted = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
                      } else if (digits.length > 3) {
                        formatted = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
                      } else if (digits.length > 0) {
                        formatted = `(${digits}`;
                      }
                      setFormData({ ...formData, phone: formatted });
                    }}
                    maxLength={14}
                    className={`w-full bg-vault-dark border rounded px-4 py-3 text-white focus:outline-none focus:border-vault-gold transition ${errors.contact && !formData.email ? 'border-red-500' : 'border-white/10'}`}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>
              {errors.contact && <p className="text-red-500 text-sm mb-4 text-center">{errors.contact}</p>}
              <p className="text-gray-500 text-sm mb-6 text-center">* Email or phone required so we can reach you</p>
              <button type="submit" disabled={submitting} className="btn-primary w-full text-lg disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? 'Submitting...' : 'Get in Touch'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-vault-black border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <VaultLogo />
              <span className="text-lg font-semibold tracking-wider">VAULT DESIGN</span>
            </div>
            <p className="text-gray-500 text-sm">
              Custom Luxury Home Gyms • Utah
            </p>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Vault Design. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}

// Components

function VaultLogo({ large = false }: { large?: boolean }) {
  const size = large ? 'w-24 h-24' : 'w-10 h-10';
  return (
    <img src="/logo.png" alt="Vault Design" className={size} />
  );
}

function ProblemCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-vault-black border border-white/10 rounded-lg p-8 hover:border-white/20 transition">
      <div className="mb-4 text-vault-gold">{icon}</div>
      <h3 className="text-xl font-semibold mb-3">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </div>
  );
}

function IconHome() {
  return (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function IconDollar() {
  return (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function IconBlueprint() {
  return (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
    </svg>
  );
}

function ProcessStep({ number, title, description }: { number: string; title: string; description: string }) {
  return (
    <div className="text-center">
      <div className="text-5xl font-display text-vault-gold/30 mb-4">{number}</div>
      <h3 className="text-xl font-semibold mb-4">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </div>
  );
}

function FeatureItem({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-2 h-2 bg-vault-gold rounded-full" />
      <span className="text-gray-300">{text}</span>
    </div>
  );
}

export default App;
